import styled, { css, keyframes } from "styled-components";
import { deviceMax } from "../../../utils/device";

interface IPlay {
  /** Animations are emitted only once the panel has entered the viewport. */
  $play?: boolean;
}

/* ---------------------------------------------------------------------------
 * Keyframes
 * ------------------------------------------------------------------------- */

const typing = keyframes`
  to {
    width: 100%;
  }
`;

/* Ends transparent so the caret is gone once typing finishes. */
const caret = keyframes`
  0%,
  49% {
    border-right-color: #7b86c6;
  }
  50%,
  100% {
    border-right-color: transparent;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const dotPulse = keyframes`
  0%,
  100% {
    opacity: 0.35;
    transform: translateY(0);
  }
  50% {
    opacity: 1;
    transform: translateY(-3px);
  }
`;

/* Anchored to the baseline via transform-origin: bottom on ChartBar, so
   growing from 0 reads as the bar rising into place rather than the whole
   element scaling from its center. */
const growBar = keyframes`
  from {
    transform: scaleY(0);
  }
  to {
    transform: scaleY(1);
  }
`;

/* ---------------------------------------------------------------------------
 * Shell
 * ------------------------------------------------------------------------- */

export const Panel = styled.div(
  ({ theme }) => css`
    background: ${theme.colors.white};
    border: 1px solid rgba(255, 255, 255, 0.27);
    border-radius: ${theme.layout.radius.panel};
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.13);
    color: ${theme.colors.brand.ink};
    font-family: ${theme.typography.fonts.body};
    overflow: hidden;
    position: relative;
    text-align: left;

    @media ${deviceMax.phoneMax} {
      border-radius: ${theme.layout.radius.card};
    }
  `
);

export const Header = styled.div`
  padding: 16px 18px 14px;

  @media ${deviceMax.tabletWideMax} {
    padding: 13px 14px 11px;
  }
`;

export const HeaderTitle = styled.p(
  ({ theme }) => css`
    color: ${theme.colors.brand.deep};
    font-size: 15px;
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.3;
    margin: 0;

    @media ${deviceMax.tabletWideMax} {
      font-size: 13px;
    }
  `
);

export const Tabs = styled.div(
  ({ theme }) => css`
    border-bottom: 1px solid ${theme.colors.brand.line};
    display: flex;
    gap: 22px;
    padding: 0 18px;

    @media ${deviceMax.tabletWideMax} {
      gap: 14px;
      padding: 0 14px;
    }

    @media ${deviceMax.compactMax} {
      gap: 11px;
    }
  `
);

interface ITab {
  $active?: boolean;
}

export const Tab = styled.span<ITab>(
  ({ theme, $active }) => css`
    align-items: center;
    border-bottom: 2px solid
      ${$active ? theme.colors.brand.action : "transparent"};
    color: ${$active ? theme.colors.brand.action : theme.colors.brand.muted};
    display: inline-flex;
    font-size: 12px;
    font-weight: ${$active ? 650 : 500};
    gap: 6px;
    padding: 11px 0 9px;
    white-space: nowrap;

    svg {
      height: 14px;
      width: 14px;
    }

    @media ${deviceMax.tabletWideMax} {
      font-size: 10px;
      gap: 5px;

      svg {
        height: 12px;
        width: 12px;
      }
    }
  `
);

export const Toolbar = styled.div(
  ({ theme }) => css`
    background: ${theme.colors.white};
    border-bottom: 1px solid ${theme.colors.brand.line};
    padding: 11px 18px;

    @media ${deviceMax.tabletWideMax} {
      padding: 9px 14px;
    }
  `
);

export const Chip = styled.span(
  ({ theme }) => css`
    align-items: center;
    border: 1px solid ${theme.colors.brand.line};
    border-radius: 999px;
    color: ${theme.colors.brand.deep};
    display: inline-flex;
    font-size: 11px;
    font-weight: 600;
    gap: 6px;
    padding: 6px 12px;

    svg {
      height: 12px;
      width: 12px;
    }

    @media ${deviceMax.tabletWideMax} {
      font-size: 10px;
      padding: 5px 10px;
    }
  `
);

/* ---------------------------------------------------------------------------
 * Message area
 * ------------------------------------------------------------------------- */

export const Messages = styled.div(
  ({ theme }) => css`
    background: ${theme.colors.brand.soft};
    display: flex;
    flex-direction: column;
    /* Reserved so nothing reflows as the sequence plays. Re-tune once the
       real question and answer copy are in place. */
    min-height: 320px;
    padding: 16px 18px 18px;

    @media ${deviceMax.tabletWideMax} {
      min-height: 272px;
      padding: 13px 14px 14px;
    }

    @media ${deviceMax.phoneMax} {
      min-height: 288px;
    }
  `
);

/**
 * Static chrome, not part of the animated sequence - production always
 * shows it, so it holds from the first frame.
 */
export const TodayDivider = styled.p(
  ({ theme }) => css`
    color: ${theme.colors.brand.muted};
    font-size: 10px;
    font-weight: 650;
    margin: 0 0 14px;
    text-align: center;

    @media ${deviceMax.tabletWideMax} {
      font-size: 9px;
      margin-bottom: 11px;
    }
  `
);

export const UserRow = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: column;
`;

export const UserBubble = styled.div<IPlay>(
  ({ $play }) => css`
    background: #e9edfb;
    border-radius: 12px;
    color: #2c3e50;
    font-size: 12px;
    line-height: 1.55;
    max-width: 86%;
    opacity: 0;
    padding: 9px 13px;

    ${$play &&
    css`
      animation: ${fadeIn} 0.2s ease-out var(--enter) both;
    `};

    @media ${deviceMax.tabletWideMax} {
      font-size: 11px;
    }
  `
);

/** Sets the bubble width from the full string while staying invisible. */
export const TypeGhost = styled.span`
  visibility: hidden;
  white-space: nowrap;
`;

export const TypeWrap = styled.span`
  display: inline-block;
  max-width: 100%;
  position: relative;
`;

export const TypeReveal = styled.span<IPlay>(
  ({ $play }) => css`
    border-right: 1.5px solid transparent;
    height: 100%;
    left: 0;
    overflow: hidden;
    position: absolute;
    top: 0;
    white-space: nowrap;
    width: 0;

    ${$play &&
    css`
      animation: ${typing} var(--type-duration) steps(var(--chars), end)
          var(--enter) both,
        ${caret} var(--caret-period) step-end var(--enter) var(--caret-blinks)
          both;
    `};
  `
);

/** Fallback for long questions and narrow screens: no single-line typing. */
export const WrappedQuestion = styled.span`
  white-space: normal;
`;

export const Stamp = styled.span<IPlay>(
  ({ theme, $play }) => css`
    color: ${theme.colors.brand.muted};
    font-size: 10px;
    margin-top: 6px;
    opacity: 0;

    ${$play &&
    css`
      animation: ${fadeIn} 0.3s ease-out var(--enter) both;
    `};

    @media ${deviceMax.tabletWideMax} {
      font-size: 9px;
    }
  `
);

export const UserStamp = styled(Stamp)`
  align-self: flex-end;
`;

/**
 * Stacks the thinking dots and the answer card in one grid cell so the card
 * can replace the dots without shifting anything below it.
 */
export const AssistantSlot = styled.div`
  display: grid;
  margin-top: 16px;

  > * {
    grid-area: 1 / 1;
  }

  @media ${deviceMax.tabletWideMax} {
    margin-top: 12px;
  }
`;

export const Thinking = styled.div<IPlay>(
  ({ theme, $play }) => css`
    align-items: center;
    align-self: start;
    background: ${theme.colors.white};
    border: 1px solid ${theme.colors.brand.line};
    border-radius: 12px;
    display: flex;
    gap: 5px;
    justify-self: start;
    opacity: 0;
    padding: 13px 15px;

    ${$play &&
    css`
      /* fadeOut is 'forwards', not 'both': a backwards fill would win the
         cascade and show the dots from the very first frame. */
      animation: ${fadeIn} 0.2s ease-out var(--dots-in) both,
        ${fadeOut} 0.2s ease-out var(--dots-out) forwards;
    `};
  `
);

export const Dot = styled.span<IPlay>(
  ({ theme, $play }) => css`
    background: ${theme.colors.brand.muted};
    border-radius: 50%;
    display: block;
    height: 5px;
    opacity: 0.35;
    width: 5px;

    ${$play &&
    css`
      animation: ${dotPulse} 0.5s ease-in-out var(--dot-delay) 2 both;
    `};
  `
);

export const AssistantRow = styled.div<IPlay>(
  ({ $play }) => css`
    align-items: flex-start;
    display: flex;
    gap: 9px;
    opacity: 0;

    ${$play &&
    css`
      animation: ${fadeIn} 0.3s ease-out var(--enter) both;
    `};
  `
);

export const Avatar = styled.span(
  ({ theme }) => css`
    align-items: center;
    background: ${theme.colors.white};
    border: 1px solid ${theme.colors.brand.line};
    border-radius: 50%;
    color: ${theme.colors.brand.action};
    display: flex;
    flex: none;
    height: 26px;
    justify-content: center;
    width: 26px;

    svg {
      height: 14px;
      width: 14px;
    }

    @media ${deviceMax.tabletWideMax} {
      height: 22px;
      width: 22px;

      svg {
        height: 12px;
        width: 12px;
      }
    }
  `
);

export const Card = styled.div(
  ({ theme }) => css`
    background: ${theme.colors.white};
    border: 1px solid ${theme.colors.brand.line};
    border-radius: 12px;
    flex: 1;
    padding: 13px 15px;

    @media ${deviceMax.tabletWideMax} {
      padding: 11px 12px;
    }
  `
);

export const Paragraph = styled.p<IPlay>(
  ({ theme, $play }) => css`
    color: ${theme.colors.brand.ink};
    font-size: 12px;
    line-height: 1.6;
    margin: 0;
    opacity: 0;

    & + & {
      margin-top: 9px;
    }

    ${$play &&
    css`
      animation: ${fadeUp} 0.4s ease-out var(--enter) both;
    `};

    @media ${deviceMax.tabletWideMax} {
      font-size: 11px;
    }
  `
);

/**
 * Deliberately inline, not gated behind a click - see AnalystDemo's header
 * comment. Fades in with the row (ChartRow), same as the answer card's
 * first paragraph fades in with AssistantRow: the label and the row share
 * one entrance, only the bars below get their own staggered animation.
 */
export const ChartCaption = styled.p<IPlay>(
  ({ theme, $play }) => css`
    color: ${theme.colors.brand.muted};
    font-size: 10px;
    margin: 0 0 10px;
    opacity: 0;

    ${$play &&
    css`
      animation: ${fadeIn} 0.3s ease-out var(--enter) both;
    `};

    @media ${deviceMax.tabletWideMax} {
      font-size: 9px;
      margin-bottom: 8px;
    }
  `
);

/**
 * Bars only - no labels inside this row. Nesting a label under each bar in
 * the same flex column made every bar's height percentage resolve against
 * (container height - label - gap) instead of the full height, so any bar
 * taller than that leftover space silently got clamped to it: the 70% and
 * 100% bars rendered at the same clamped height. Labels get their own row
 * (ChartBarLabels) below instead.
 */
export const ChartBars = styled.div`
  align-items: flex-end;
  display: flex;
  gap: 16px;
  height: 48px;

  @media ${deviceMax.tabletWideMax} {
    gap: 12px;
    height: 40px;
  }
`;

interface IChartBar {
  /** Highlights the tallest bar; the rest stay neutral grey. */
  $accent?: boolean;
}

/**
 * Height is set inline (see AnalystDemo, computed from DEMO_CHART_BARS) as
 * a percentage of ChartBars' fixed height; the grow animation then scales
 * that resting height from 0.
 */
export const ChartBar = styled.span<IPlay & IChartBar>(
  ({ theme, $play, $accent }) => css`
    background: ${$accent ? theme.colors.brand.action : "#DDE3EA"};
    border-radius: 3px 3px 0 0;
    display: block;
    flex: 1;
    transform: scaleY(0);
    transform-origin: bottom;

    ${$play &&
    css`
      animation: ${growBar} 0.5s ease-out var(--enter) both;
    `};
  `
);

export const ChartBarLabels = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 6px;

  @media ${deviceMax.tabletWideMax} {
    gap: 12px;
  }
`;

export const ChartBarLabel = styled.span(
  ({ theme }) => css`
    color: ${theme.colors.brand.muted};
    flex: 1;
    font-size: 9px;
    line-height: 1.3;
    text-align: center;

    @media ${deviceMax.tabletWideMax} {
      font-size: 8px;
    }
  `
);

export const AssistantStamp = styled(Stamp)`
  margin-left: 35px;

  @media ${deviceMax.tabletWideMax} {
    margin-left: 31px;
  }
`;

export const ChartRow = styled(AssistantRow)`
  margin-top: 14px;
`;

/* ---------------------------------------------------------------------------
 * Input bar (decorative - no focusable elements)
 * ------------------------------------------------------------------------- */

export const InputBar = styled.div(
  ({ theme }) => css`
    align-items: center;
    background: ${theme.colors.white};
    border-top: 1px solid ${theme.colors.brand.line};
    display: flex;
    gap: 10px;
    padding: 12px 18px;

    @media ${deviceMax.tabletWideMax} {
      padding: 10px 14px;
    }
  `
);

export const InputField = styled.span(
  ({ theme }) => css`
    align-items: center;
    border: 1px solid ${theme.colors.brand.line};
    border-radius: 999px;
    color: ${theme.colors.brand.muted};
    display: flex;
    flex: 1;
    font-size: 11px;
    justify-content: space-between;
    min-width: 0;
    padding: 9px 9px 9px 15px;

    @media ${deviceMax.tabletWideMax} {
      font-size: 10px;
      padding-left: 12px;
    }
  `
);

export const InputText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SendCircle = styled.span`
  align-items: center;
  background: #e9edfb;
  border-radius: 50%;
  color: #9aa4d4;
  display: flex;
  flex: none;
  height: 26px;
  justify-content: center;
  width: 26px;

  svg {
    height: 13px;
    width: 13px;
  }

  @media ${deviceMax.tabletWideMax} {
    height: 23px;
    width: 23px;
  }
`;

export const Glyph = styled.svg`
  flex: none;
`;

/* ---------------------------------------------------------------------------
 * Reduced motion: render the finished frame, no sequence.
 * globalStyles already disables animations, so the end state is set here.
 * ------------------------------------------------------------------------- */

export const Chrome = styled.div`
  /*
   * Below the phone breakpoint the question is allowed to wrap, so the
   * single-line steps() typewriter is swapped for the plain bubble fade.
   * Wrapping beats typing here - the copy must never truncate mid-sentence.
   */
  @media ${deviceMax.phoneMax} {
    ${TypeReveal} {
      display: none;
    }

    ${TypeGhost} {
      position: static;
      visibility: visible;
      white-space: normal;
    }
  }

  /*
   * Reduced motion: hold the finished frame. globalStyles already disables
   * every animation, so the end state has to be declared explicitly.
   */
  @media (prefers-reduced-motion: reduce) {
    ${UserBubble},
    ${Stamp},
    ${AssistantRow},
    ${Paragraph},
    ${ChartCaption},
    ${ChartBar} {
      /* transform: none is the same identity transform as ChartBar's
         resting scaleY(1) - shows the bar at full height. */
      opacity: 1;
      transform: none;
    }

    ${TypeReveal} {
      display: none;
    }

    ${TypeGhost} {
      position: static;
      visibility: visible;
      white-space: normal;
    }

    ${Thinking} {
      display: none;
    }
  }
`;
